/**
 * The thread as a list of rows: a day separator before each calendar day, the new-messages marker
 * before the first message the trainer had not read, and each message knowing whether it opens and
 * closes its run.
 *
 * ── A run is one sender on one day ───────────────────────────────────────────────────────────
 * The canon groups consecutive messages of the same side on the same day; the first of a run gets
 * the gap above it and the last gets the tail corner and the time under it. The marker breaks a run
 * as well, because a run that straddles it would carry its tail on the wrong side of the line. A line
 * the app wrote (`system`) stands alone: it is stored as the trainer's but is nobody's bubble.
 *
 * ── The marker is anchored to a message, never to a count ────────────────────────────────────
 * It is placed when a conversation is opened (`unreadAnchor`) and stands before THAT message until
 * the trainer answers. Recounting from `unreadCount` on every render would walk it down the thread
 * as the server zeroes the count after the read mark.
 */

import type { ChatMessage } from './chatMessage';

export type ThreadItem =
  | { readonly type: 'day'; readonly key: string; readonly dayKey: string; readonly label: DayLabel }
  | {
      readonly type: 'divider';
      readonly key: string;
      readonly count: number;
      readonly since: string;
    }
  | {
      readonly type: 'message';
      readonly key: string;
      readonly message: ChatMessage;
      readonly first: boolean;
      readonly last: boolean;
    };

/** `today` / `yesterday` are worded by the caller; `date` carries the civil date to format. */
export type DayLabel =
  | { readonly kind: 'today' }
  | { readonly kind: 'yesterday' }
  | { readonly kind: 'date'; readonly at: string };

/** The reader's calendar day of an instant, as `YYYY-MM-DD` in the browser's zone. */
export function localDayKey(iso: string): string {
  const at = new Date(iso);
  const y = at.getFullYear();
  const m = String(at.getMonth() + 1).padStart(2, '0');
  const d = String(at.getDate()).padStart(2, '0');
  return `${String(y)}-${m}-${d}`;
}

export function dayLabelOf(iso: string, now: Date): DayLabel {
  const key = localDayKey(iso);
  if (key === localDayKey(now.toISOString())) return { kind: 'today' };
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (key === localDayKey(yesterday.toISOString())) return { kind: 'yesterday' };
  return { kind: 'date', at: iso };
}

/**
 * Which message the marker stands before, chosen once when the conversation is opened: the oldest
 * of the last `unread` messages from the other side. `null` with nothing unread, and `null` when the
 * thread does not hold that many of theirs (an older page would be needed to place it honestly).
 */
export function unreadAnchor(messages: readonly ChatMessage[], unread: number): string | null {
  if (unread <= 0) return null;
  let seen = 0;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message === undefined || message.mine) continue;
    seen += 1;
    if (seen === unread) return message.key;
  }
  return null;
}

export function buildThreadItems(
  messages: readonly ChatMessage[],
  anchorKey: string | null,
  now: Date,
): ThreadItem[] {
  const items: ThreadItem[] = [];
  const anchorIndex = anchorKey === null ? -1 : messages.findIndex((m) => m.key === anchorKey);
  let lastDay: string | null = null;

  messages.forEach((message, index) => {
    const day = localDayKey(message.createdAt);
    if (day !== lastDay) {
      items.push({ type: 'day', key: `day-${day}`, dayKey: day, label: dayLabelOf(message.createdAt, now) });
      lastDay = day;
    }
    if (index === anchorIndex) {
      const count = messages.slice(index).filter((m) => !m.mine).length;
      items.push({ type: 'divider', key: 'divider', count, since: message.createdAt });
    }
    const previous = messages[index - 1];
    const next = messages[index + 1];
    const joins = (a: ChatMessage | undefined, b: ChatMessage | undefined) =>
      a !== undefined &&
      b !== undefined &&
      a.mine === b.mine &&
      !a.system &&
      !b.system &&
      localDayKey(a.createdAt) === localDayKey(b.createdAt);
    const first = !joins(previous, message) || index === anchorIndex;
    const last = !joins(message, next) || index + 1 === anchorIndex;
    items.push({ type: 'message', key: message.key, message, first, last });
  });

  return items;
}

/** The `HH:MM` the bubble's meta line prints. */
export function clockOf(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}
